import {Box, Tab, Tabs, Typography} from '@mui/material';
import {
  TabData,
  TabPanelProps,
} from '../../../user-management/edit-user/EditUser';
import './DocDetails.scss';
import React, {useEffect} from 'react';
import {t} from 'i18next';
import Metadata from './metadata/Metadata';
import Folder from './folder/Folder';
import History from './history/History';
import {useSignalStore} from '../../../PortalPopout/hooks/useSignalStore';
import {ReactComponent as ExportIcon} from '../../../../assets/images/export.svg';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import ArrButton from '../../../common/arr-button/ArrButton';

interface DocDetailsProps {
  panelId?: string;
  onHistoryExport?: (documentId?: number | string) => void;
}

function TabPanel(props: Readonly<TabPanelProps>) {
  const {children, value, index, ...other} = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      className={`tabpanel-${value === index ? 'active' : 'hidden'}`}
      {...other}
      data-testid={`doc-details-di-tab-${index}`}
    >
      {value === index && (
        <div data-testid={`doc-details-div-tab-child-${index}`}>{children}</div>
      )}
    </div>
  );
}
const enDocDetails = 'docView.docDetails';
const initialActiveTab = 0;

// DocDetails Component
export const DocDetails = ({onHistoryExport, panelId}: DocDetailsProps) => {
  const documentId = useSelector(
    (state: RootState) => state.docView?.activeDoc?.documentId
  );
  const {isShowFoldersTab, metadataView, docHistoryView} = useSelector(
    (state: RootState) => state.docView.docViewConfiguration
  );
  const [tabValue, setTabValue] = useSignalStore(
    'docDetails-tabValue',
    initialActiveTab
  );

  useEffect(() => {
    setTabValue(initialActiveTab);
  }, [documentId]);

  const handleChangeTab = (
    event: React.SyntheticEvent,
    newTabValue: number
  ) => {
    setTabValue(newTabValue);
  };

  const tabData: TabData[] = [
    {
      id: 'metadata-tab',
      label: (
        <Typography
          className="typo-text subhead5_size tab-label"
          data-testid="metadata-tab-label"
        >
          {t(`${enDocDetails}.metadata`)}
        </Typography>
      ),
      content: <Metadata />,
      enabled: metadataView,
    },
    {
      id: 'history-tab',
      label: (
        <Typography
          className="typo-text subhead5_size tab-label"
          data-testid="history-tab-label"
        >
          {t(`${enDocDetails}.history`)}
        </Typography>
      ),
      //   Will remove once implemented
      content: <History />,
      enabled: docHistoryView,
    },
    {
      id: 'folder-tab',
      label: (
        <Typography
          className="typo-text subhead5_size tab-label"
          data-testid="folder-tab-label"
        >
          {t(`${enDocDetails}.folder`)}
        </Typography>
      ),
      //   Will remove once implemented
      content: <Folder />,
      enabled: isShowFoldersTab,
    },
  ].filter((tab) => tab.enabled);
  return (
    <Box className="doc-details-container">
      <Tabs
        value={tabValue || 0}
        onChange={handleChangeTab}
        aria-label="tabs"
        className="tabs"
      >
        {tabData.map((tab) => (
          <Tab
            id={tab.id}
            aria-controls={`tabpanel-${tab.id}`}
            key={tab.id}
            label={tab.label}
            className="tab"
            data-testid={tab.id}
          />
        ))}
        <Box
          className="tabs__action-buttons"
          data-testid="tab-action-buttons"
          aria-role="button"
        >
          {tabValue ===
            tabData.findIndex((tab) => tab.id === 'history-tab') && (
            <ArrButton
              size="small"
              className="tabs__action-button"
              dataTestId="export-history-button"
              startIcon={<ExportIcon />}
              variant="text"
              onClick={() => onHistoryExport && onHistoryExport(documentId)}
              data-tab-id="export-history-button"
              data-tab-group={panelId}
            >
              <Typography
                data-testid="export-history-button-label"
                className="tabs__action-button-label typo-text subhead5_size"
              >
                {t(`${enDocDetails}.exportHistory`)}
              </Typography>
            </ArrButton>
          )}
        </Box>
      </Tabs>
      {tabData.map((tab, index) => (
        <TabPanel key={tab.id} value={tabValue || 0} index={index}>
          {tab.content}
        </TabPanel>
      ))}
    </Box>
  );
};
export default React.memo(DocDetails);
